import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useConsent } from '../contexts/ConsentContext';

/**
 * Bannière de consentement.
 *
 * Deux règles dictent la forme de cet écran, et elles ne sont pas
 * décoratives : refuser doit demander le même effort qu'accepter — même
 * taille, même poids visuel, un seul clic de chaque côté — et aucune case
 * facultative n'est cochée d'avance. Un bouton de refus en petit lien grisé
 * suffit à rendre le consentement invalide.
 *
 * Elle occupe le bas de l'écran plutôt que de le bloquer : un mur de
 * consentement devant une boutique se négocie mal, et l'accès au site ne
 * peut pas être conditionné à l'acceptation de la publicité.
 */
export default function BanniereConsentement() {
  const { t, language } = useLanguage();
  const {
    demande, reglages, publiciteConfiguree, choix,
    enregistrer, toutAccepter, toutRefuser, ouvrirReglages,
  } = useConsent();

  // Les cases partent décochées : le consentement se donne, il ne se présume.
  const [mesure, setMesure] = useState(false);
  const [publicite, setPublicite] = useState(false);
  const premierBouton = useRef(null);

  // À la réouverture depuis le pied de page, les cases reflètent le choix en
  // cours : le visiteur vient modifier un réglage, pas repartir de zéro.
  useEffect(() => {
    if (reglages) {
      setMesure(choix.mesure);
      setPublicite(choix.publicite);
    }
  }, [reglages, choix.mesure, choix.publicite]);

  useEffect(() => {
    if (demande && premierBouton.current) premierBouton.current.focus();
  }, [demande, reglages]);

  if (!demande) return null;

  const lienPolitique = (
    <Link
      to="/confidentialite"
      className="underline hover:text-primary transition-colors"
      data-testid="consent-policy-link"
    >
      {t('cookiesEnSavoirPlus')}
    </Link>
  );

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="consentement-titre"
      className="fixed bottom-0 left-0 right-0 z-[60] bg-white border-t border-black/10 shadow-[0_-4px_24px_rgba(0,0,0,0.08)]"
      data-testid="consent-banner"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 sm:py-5">
        <h2 id="consentement-titre" className="text-base font-bold text-text_primary font-heading">
          {t('cookiesTitre')}
        </h2>
        <p className="mt-1.5 text-sm text-text_primary/70 leading-relaxed">
          {t('cookiesTexte')} {lienPolitique}
        </p>

        {reglages && (
          <div className="mt-4 space-y-3" data-testid="consent-details">
            <Rubrique
              titre={t('cookiesNecessaireTitre')}
              texte={t('cookiesNecessaireTexte')}
              etat={t('cookiesNecessaireEtat')}
            />
            <Rubrique
              titre={t('cookiesMesureTitre')}
              texte={t('cookiesMesureTexte')}
              coche={mesure}
              onChange={setMesure}
              testid="consent-toggle-mesure"
            />
            {publiciteConfiguree && (
              <Rubrique
                titre={t('cookiesPubliciteTitre')}
                texte={t('cookiesPubliciteTexte')}
                coche={publicite}
                onChange={setPublicite}
                testid="consent-toggle-publicite"
              />
            )}
          </div>
        )}

        {/* Accepter et refuser : même taille, même poids, un clic chacun. */}
        <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:items-center">
          <button
            ref={premierBouton}
            type="button"
            onClick={toutAccepter}
            className="px-5 py-2.5 text-sm font-semibold text-white bg-primary hover:opacity-90 transition-opacity"
            data-testid="consent-accept-all"
          >
            {t('cookiesToutAccepter')}
          </button>
          <button
            type="button"
            onClick={toutRefuser}
            className="px-5 py-2.5 text-sm font-semibold text-white bg-text_primary hover:opacity-90 transition-opacity"
            data-testid="consent-reject-all"
          >
            {t('cookiesToutRefuser')}
          </button>
          {reglages ? (
            <button
              type="button"
              onClick={() => enregistrer({ mesure, publicite })}
              className="px-5 py-2.5 text-sm font-semibold text-text_primary border border-text_primary/30 hover:border-text_primary transition-colors"
              data-testid="consent-save"
            >
              {t('cookiesEnregistrer')}
            </button>
          ) : (
            <button
              type="button"
              onClick={ouvrirReglages}
              className="px-5 py-2.5 text-sm font-semibold text-text_primary border border-text_primary/30 hover:border-text_primary transition-colors"
              data-testid="consent-customise"
            >
              {t('cookiesPersonnaliser')}
            </button>
          )}
          <span className="sm:ml-auto text-xs text-text_primary/50">
            {language === 'en' ? 'AfriOne BD · Liège' : 'AfriOne BD · Liège'}
          </span>
        </div>
      </div>
    </div>
  );
}

/** Une rubrique du panneau détaillé : soit une case, soit un état figé. */
function Rubrique({ titre, texte, etat, coche, onChange, testid }) {
  const interactif = typeof onChange === 'function';
  return (
    <div className="flex items-start gap-3 p-3 bg-background border border-black/5">
      {interactif ? (
        <input
          type="checkbox"
          checked={coche}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-0.5 w-4 h-4 accent-primary flex-shrink-0 cursor-pointer"
          aria-label={titre}
          data-testid={testid}
        />
      ) : (
        <span className="mt-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary flex-shrink-0">
          {etat}
        </span>
      )}
      <div className="min-w-0">
        <div className="text-sm font-semibold text-text_primary">{titre}</div>
        <p className="text-xs text-text_primary/60 leading-relaxed mt-0.5">{texte}</p>
      </div>
    </div>
  );
}
